import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { ScoreBadge, CRITERIO_LABELS, TicketLink, ProvisionalBadge } from './AgentQualityCalibration';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  processing: { label: 'Procesando', color: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Completado', color: 'bg-green-100 text-green-700' },
  partial_complete: { label: 'Parcial', color: 'bg-yellow-100 text-yellow-700' },
  failed: { label: 'Fallido', color: 'bg-red-100 text-red-700' },
  timeout: { label: 'Timeout', color: 'bg-orange-100 text-orange-700' }
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_LABELS[status] || { label: status || 'Desconocido', color: 'bg-gray-100 text-gray-700' };
  return <Badge className={cfg.color} data-testid={`batch-status-${status}`}>{cfg.label}</Badge>;
};

const QuantityCell = ({ label, value, testId }) => (
  <div className="text-center" data-testid={testId}>
    <p className="text-xl font-bold text-gray-900">{value ?? '-'}</p>
    <p className="text-xs text-gray-500">{label}</p>
  </div>
);

const BatchCard = ({ batch, agreementRate, totalCalibrated }) => {
  const [expanded, setExpanded] = useState(false);
  const period = batch.date_from || batch.date_to
    ? `${batch.date_from || '...'} → ${batch.date_to || '...'}`
    : 'Sin rango de fechas';

  return (
    <Card data-testid={`batch-card-${batch.batch_id}`}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              {batch.instance_name}
              <StatusBadge status={batch.status} />
              <ProvisionalBadge agreementRate={agreementRate} totalCalibrated={totalCalibrated} />
            </CardTitle>
            <CardDescription>
              {new Date(batch.started_at).toLocaleString('es-AR')} · {period}
              {batch.agent_id_filter && ` · Agente ID: ${batch.agent_id_filter}`}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            data-testid={`batch-expand-btn-${batch.batch_id}`}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {expanded ? 'Ocultar' : 'Ver detalle'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Cantidades */}
        <div className="grid grid-cols-5 gap-2 bg-gray-50 rounded-lg p-3">
          <QuantityCell label="Solicitados" value={batch.quantities.requested} testId={`batch-requested-${batch.batch_id}`} />
          <QuantityCell label="En HubSpot" value={batch.quantities.found_in_hubspot} testId={`batch-found-${batch.batch_id}`} />
          <QuantityCell label="Nuevos" value={batch.quantities.new_tickets} testId={`batch-new-${batch.batch_id}`} />
          <QuantityCell label="Ya existían" value={batch.quantities.already_scored} testId={`batch-existing-${batch.batch_id}`} />
          <QuantityCell label="Reemplazados" value={batch.quantities.existing_scores_replaced} testId={`batch-replaced-${batch.batch_id}`} />
        </div>

        {/* Calibración + Score global */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="text-center border rounded-lg p-2">
            <p className="text-lg font-bold">{batch.calibration_summary.calibrated}/{batch.calibration_summary.total}</p>
            <p className="text-xs text-gray-500">Calibrados</p>
          </div>
          <div className="text-center border rounded-lg p-2">
            <p className="text-lg font-bold">{batch.score_avg_llm ?? '-'}</p>
            <p className="text-xs text-gray-500">Promedio LLM</p>
          </div>
          <div className="text-center border rounded-lg p-2">
            <p className="text-lg font-bold">{batch.score_avg_humano ?? '-'}</p>
            <p className="text-xs text-gray-500">Promedio Humano</p>
          </div>
          <div className="text-center border rounded-lg p-2" data-testid={`batch-agreement-${batch.batch_id}`}>
            <p className="text-lg font-bold">
              {batch.calibration_summary.calibrated > 0 ? `${batch.agreement_global.agreement_rate ?? 0}%` : '-'}
            </p>
            <p className="text-xs text-gray-500">Acuerdo LLM-Humano</p>
          </div>
        </div>

        {expanded && (
          <div className="space-y-4 pt-2 border-t">
            {/* Acuerdo por eje */}
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Acuerdo LLM-Humano por Eje</p>
              {Object.keys(batch.criterios_metrics).length === 0 ? (
                <p className="text-sm text-gray-400">Aún no hay tickets calibrados en este batch</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(batch.criterios_metrics).map(([codigo, m]) => (
                    <div key={codigo} className="border rounded-lg p-2 text-sm" data-testid={`batch-criterio-${batch.batch_id}-${codigo}`}>
                      <p className="font-medium">{CRITERIO_LABELS[codigo]?.icon} {CRITERIO_LABELS[codigo]?.name}</p>
                      <p className="text-gray-500">Acuerdo: {m.agreement_rate}% ({m.agreements}/{m.total_calibrated})</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tickets incluidos */}
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Tickets Incluidos ({batch.tickets.length})</p>
              <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
                {batch.tickets.map((t) => (
                  <div
                    key={t.ticket_id}
                    className="flex items-center justify-between px-3 py-2 text-sm"
                    data-testid={`batch-ticket-row-${t.ticket_id}`}
                  >
                    <TicketLink ticketId={t.ticket_id} />
                    <span className="text-gray-500 truncate max-w-[140px]">{t.agent_name || 'Sin agente'}</span>
                    <div className="flex items-center gap-2">
                      <ScoreBadge score={t.score_global} size="sm" />
                      <ProvisionalBadge agreementRate={agreementRate} totalCalibrated={totalCalibrated} />
                      {t.score_global_calibrado != null && (
                        <span className="text-xs text-gray-400">→ {t.score_global_calibrado} (humano)</span>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {t.calibration_status === 'calibrated' ? 'Calibrado' : 'Pendiente'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const BatchHistory = ({ token, instances, agreementRate = 0, totalCalibrated = 0 }) => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [instanceFilter, setInstanceFilter] = useState('');

  const loadBatches = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = instanceFilter ? `?instance=${instanceFilter}` : '';
      const response = await fetch(`${API_URL}/api/agent-quality/calibration/batches${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('No se pudo cargar el historial de batches');
      const data = await response.json();
      setBatches(data.batches || []);
    } catch (err) {
      console.error('Error cargando historial de batches:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, instanceFilter]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">Historial de Batches</h3>
          <p className="text-sm text-gray-500">
            Cada corrida de "Generar Batch" queda registrada como una unidad independiente, sin mezclarse con las demás. Solo lectura.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="border rounded-md px-3 py-2 text-sm"
            value={instanceFilter}
            onChange={(e) => setInstanceFilter(e.target.value)}
            data-testid="batch-history-instance-filter"
          >
            <option value="">Todas las instancias</option>
            {(instances || []).map((inst) => (
              <option key={inst.slug} value={inst.slug}>{inst.name}</option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={loadBatches} disabled={loading} data-testid="batch-history-refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400" data-testid="batch-history-loading">Cargando historial...</p>
      ) : error ? (
        <Card>
          <CardContent className="py-8 text-center text-red-500" data-testid="batch-history-error">
            Error cargando el historial: {error}
          </CardContent>
        </Card>
      ) : batches.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-400" data-testid="batch-history-empty">
            Todavía no hay batches generados{instanceFilter ? ' para esta instancia' : ''}.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="batch-history-list">
          {batches.map((batch) => (
            <BatchCard key={batch.batch_id} batch={batch} agreementRate={agreementRate} totalCalibrated={totalCalibrated} />
          ))}
        </div>
      )}
    </div>
  );
};

export default BatchHistory;
